import React, { memo } from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styled, Theme, useTheme } from "@mui/material/styles";
import { colors, TypographyProps } from "@mui/material";
import { get } from "lodash";
import { getColorScheme } from "@mui/system/cssVars/useCurrentColorScheme";
import { width } from "@mui/system";

type palette = TypographyProps["color"];
interface MxButton2Props extends ButtonProps {
  loading?: boolean;
  textColor?: palette;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  bgColor?: palette;
  boxShadowLevel?: 0 | 1 | 2 | 3 | 4;
  /**
   * @deprecated
   */
  fontSize?: 10 | 12 | 14 | 16 | 18 | 20 | 22 | 24 | 26 | 28 | 30 | 32;
  fontWeight?: number;
  borderWidth?: number;
  borderColor?: string;
  width?: string | number;
  testId?: string;
  gradientMode?: {
    start: palette;
    end: palette;
    direction: "to right" | "to left" | "to top" | "to bottom";
  };
}

const shouldForwardProp = (prop: PropertyKey) =>
  ![
    "boxShadowLevel",
    "fontSize",
    "fontWeight",
    "borderWidth",
    "textColor",
    "bgColor",
    "borderColor",
    "width",
    "gradientMode"
  ].includes(prop as string);

const StyledButton = styled(Button, { shouldForwardProp })<{
  boxShadowLevel?: 0 | 1 | 2 | 3 | 4;
  fontSize?: 10 | 12 | 14 | 16 | 18 | 20 | 22 | 24 | 26 | 28 | 30 | 32;
  fontWeight?: number;
  borderWidth?: number;
  textColor?: string;
  bgColor?: string;
  borderColor?: string;
  width?: string | number;
  gradientMode?: {
    start: string;
    end: string;
    direction: "to right" | "to left" | "to top" | "to bottom";
  };
}>(
  ({
    theme,
    boxShadowLevel,
    fontSize,
    fontWeight,
    borderWidth,
    textColor = "primary",
    bgColor,
    borderColor,
    width,
    gradientMode = undefined
  }) => ({
    transition: "all 0.5",
    width: width || undefined,
    color: textColor,
    backgroundColor: bgColor,
    boxShadow: theme.shadows[boxShadowLevel || 0],
    fontFamily:
      "SF Compact Display, Inter, Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI',  'Helvetica Neue', Arial, sans-serif",
    fontWeight: fontWeight || 600,
    borderWidth: borderWidth || 0,
    borderStyle: "solid",
    borderColor: borderColor || "currentColor",
    textTransform: "none",
    background: gradientMode
      ? `linear-gradient(${gradientMode.direction}, ${gradientMode.start}, ${gradientMode.end})`
      : undefined,
    borderRadius: 100,

    "&:hover": {
      borderColor: borderColor,
      borderWidth: borderWidth || 0,
      color: textColor,
      backgroundColor: bgColor,
      background: gradientMode
        ? `linear-gradient(${gradientMode.direction}, ${gradientMode.start}, ${gradientMode.end})`
        : undefined
    },
    "&.Mui-disabled": {
      color: theme.palette.text.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
      background: theme.palette.action.disabledBackground,
      borderColor: theme.palette.action.disabled,
      cursor: "not-allowed"
    },

    "@keyframes pulse": {
      "0%": {
        transform: "scale(1)"
      },
      "50%": {
        transform: "scale(1.03)"
      },
      "100%": {
        transform: "scale(1)"
      }
    }
  })
);

const MxButton2: React.FC<MxButton2Props> = ({
  loading,
  textColor = "white",
  startIcon,
  endIcon,
  bgColor,
  boxShadowLevel = 0,
  fontSize = 12,
  fontWeight = 500,
  borderWidth = 0,
  children,
  testId = "mx-button",
  ...props
}) => {
  const theme = useTheme();
  return (
    <StyledButton
      {...props}
      size="medium"
      disabled={loading || props.disabled}
      startIcon={loading ? <CircularProgress size={24} /> : startIcon}
      endIcon={loading ? null : endIcon}
      boxShadowLevel={boxShadowLevel}
      fontSize={fontSize}
      fontWeight={fontWeight}
      borderWidth={borderWidth}
      disableRipple
      disableTouchRipple
      data-testid={testId}
      bgColor={typeof bgColor === "string" ? get(theme.palette, bgColor) : undefined}
      textColor={
        typeof textColor === "string" ? get(theme.palette, textColor) : undefined
      }
      borderColor={
        typeof textColor === "string" ? get(theme.palette, textColor) : undefined
      }
      gradientMode={
        props.gradientMode
          ? {
              start:
                typeof props.gradientMode.start === "string"
                  ? get(theme.palette, props.gradientMode.start)
                  : undefined,
              end:
                typeof props.gradientMode.end === "string"
                  ? get(theme.palette, props.gradientMode.end)
                  : undefined,
              direction: props.gradientMode.direction
            }
          : undefined
      }
    >
      {loading ? "Cargando" : children}
    </StyledButton>
  );
};

export default MxButton2;
